import { FAIcon } from '@/components'
import { Button } from '@/components/ui'
import {
  ExportFormat,
  ExportFormatPrompt,
  ExportOption,
} from '@/modules/exports'
import { PERMISSIONS, usePermissions } from '@/utils'
import { useMutation } from '@apollo/client'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import { useNavigate } from 'react-router'
import { EXPORT_EVENT_REGISTRATIONS_MUTATION } from '../mutations'
import type {
  ExportEventRegistrationsMutation,
  ExportEventRegistrationsMutationVariables,
} from '../types.graphql'

interface Props {
  eventId: string
}

/**
 * Exports the answers to one event. A PDF comes back rendered and opens in the
 * print view; an Excel workbook is queued and picked up under "My exports" —
 * the same split every other export in the product uses.
 */
export const EventExportButton: React.FC<Props> = ({ eventId }) => {
  const translations = useTranslate({
    export: 'common.export',
    error: 'error.generic-server-error',
    queued: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const navigate = useNavigate()
  const { hasPermissions } = usePermissions()

  const [runExport, { loading }] = useMutation<
    ExportEventRegistrationsMutation,
    ExportEventRegistrationsMutationVariables
  >(EXPORT_EVENT_REGISTRATIONS_MUTATION, {
    onCompleted(data) {
      const result = data.exportEventRegistrations

      if (result?.exportTimeout) addToast('info', translations.exportTimeout)
      else if (result?.html)
        void navigate('/export', { state: { html: result.html } })
      else if (result?.ok) addToast('success', translations.queued)
      else addToast('error', translations.error)
    },
    onError() {
      addToast('error', translations.error)
    },
  })

  if (!hasPermissions(PERMISSIONS.companies.export.exportReports, true))
    return null

  async function handleExport() {
    const { data: exportFormat } = await addPrompt<ExportFormat | null>(
      resolve => <ExportFormatPrompt onSubmit={resolve} />
    )
    if (!exportFormat) return

    await runExport({
      variables: {
        event: eventId,
        exportFormat,
        exportOption:
          exportFormat === ExportFormat.PDF
            ? ExportOption.DOWNLOAD
            : ExportOption.SAVE,
      },
    })
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={loading}
      onClick={handleExport}
    >
      <FAIcon icon="download" />
      {translations.export}
    </Button>
  )
}
